/**
 * Tempo do caça-palavras (Marketing Game).
 *
 * A coluna `marketing_game_sessions.time_seconds` guarda MILISSEGUNDOS.
 * Exibição no ranking: mm:ss:mmm (minutos : segundos : milissegundos).
 */

/** Tempo decorrido em milissegundos desde `startedAtMs` (Date.now() no início). */
export function getElapsedMs(startedAtMs: number | null | undefined, nowMs = Date.now()): number {
  if (startedAtMs == null || !Number.isFinite(startedAtMs)) return 0
  return Math.max(0, Math.round(nowMs - startedAtMs))
}

/**
 * Converte valor gravado em `time_seconds` para milissegundos canônicos.
 *
 * Pode haver registros legados em segundos ou *1000 indevido.
 * Usa started_at/completed_at quando disponíveis.
 */
export function normalizeGameTimeMs(
  stored: number | null | undefined,
  startedAt?: string | null,
  completedAt?: string | null
): number {
  const raw = Math.max(0, Math.round(Number(stored) || 0))
  if (raw === 0) return 0

  let wallMs: number | null = null
  if (startedAt && completedAt) {
    const start = new Date(startedAt).getTime()
    const end = new Date(completedAt).getTime()
    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
      wallMs = end - start
    }
  }

  if (wallMs != null && wallMs > 0) {
    const asSeconds = raw * 1000
    const asMs = raw
    const asOverMs = raw / 1000
    const tol = Math.max(250, wallMs * 0.08)

    const dSec = Math.abs(asSeconds - wallMs)
    const dMs = Math.abs(asMs - wallMs)
    const dOver = Math.abs(asOverMs - wallMs)

    if (dSec <= tol && dSec <= dMs && dSec <= dOver) return Math.round(asSeconds)
    if (dOver <= tol && dOver < dMs) return Math.round(asOverMs)
    if (dMs <= tol) return Math.round(asMs)
  }

  // Sem relógio: valores < 1000 eram tipicamente segundos inteiros
  if (raw > 0 && raw < 1000) return raw * 1000

  return raw
}

function splitGameClock(totalMs: number): { minutes: number; seconds: number; milliseconds: number } {
  const ms = Math.max(0, Math.floor(Number(totalMs) || 0))
  const totalSeconds = Math.floor(ms / 1000)
  return {
    minutes: Math.floor(totalSeconds / 60),
    seconds: totalSeconds % 60,
    milliseconds: ms % 1000,
  }
}

const pad2 = (n: number) => String(n).padStart(2, '0')
const pad3 = (n: number) => String(n).padStart(3, '0')

/** Cronômetro ao vivo: somente minutos e segundos (mm:ss). */
export function formatGameClock(totalMs: number): string {
  const { minutes, seconds } = splitGameClock(totalMs)
  return `${pad2(minutes)}:${pad2(seconds)}`
}

/**
 * Ranking e resultado: minutos, segundos e milissegundos (mm:ss:mmm).
 *
 * Ex.: 65432 ms → "01:05:432"
 */
export function formatGameTime(totalMs: number): string {
  const { minutes, seconds, milliseconds } = splitGameClock(totalMs)
  return `${pad2(minutes)}:${pad2(seconds)}:${pad3(milliseconds)}`
}
